/**
 * While a workspace is halted (suspended / inactive) its status can change under an open tab — the
 * super-admin resumes it, or a payment reactivates it — and the read-only banner must not lie. This
 * re-fetches the session on a slow interval and when the tab regains focus.
 *
 * It does NOTHING for a healthy workspace (`halted` false → no timer, no listener), so the normal case
 * pays no cost at all.
 *
 * The callback is held in a ref and called through it, so the timer is armed once per `halted` change
 * and NOT re-armed every time the caller re-renders with a new function identity. (Putting `refresh` in
 * the effect's dependency list — the obvious version — clears and re-creates the interval on every
 * render of a busy layout, so a 60 s timer in a component that re-renders more often than that never
 * fires.)
 */

import { useEffect, useRef } from 'react';

export const HALTED_REFRESH_INTERVAL_MS = 60_000;

export function useHaltedWorkspaceRefresh(
  halted: boolean,
  refresh: () => unknown,
  intervalMs: number = HALTED_REFRESH_INTERVAL_MS
): void {
  const latest = useRef(refresh);
  latest.current = refresh;

  useEffect(() => {
    if (!halted) return;

    const tick = () => {
      // A background tab doesn't need to poll; it refreshes when it regains focus.
      if (document.visibilityState !== 'visible') return;
      try {
        // Promise.resolve(...).catch: a rejecting async refresh must not surface as an unhandled rejection.
        void Promise.resolve(latest.current()).catch(() => undefined);
      } catch {
        /* a synchronous throw must not kill the timer either */
      }
    };
    const timer = setInterval(tick, intervalMs);
    window.addEventListener('focus', tick);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', tick);
    };
  }, [halted, intervalMs]);
}
